import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { ConfirmPopover } from '../../library/ConfirmPopover'
import { useMediaSrc } from '../../library/useMediaSrc'
import type { VaultItem } from '../../library/vault/vaultData'
import { uploadToVault } from '../../chat/upload'
import { OF, type OfApi } from '../ofApi'
import type { OfUser } from '../posts'
import {
  buildPostBody,
  draftError,
  EMPTY_DRAFT,
  hasContent,
  hasPendingUpload,
  MEDIA_MAX,
  POLL_DAYS,
  PRICE_MIN,
  TARGET_MIN,
  TIP_PRESET_DEFAULT,
  TIP_PRESETS_MAX,
  type Draft,
  type Label,
  type MediaRef,
  type When,
} from './homeData'
import { AmountDialog, ExpiryDialog, LabelsPicker, ScheduleDialog, TagPicker } from './Pickers'
import { ContentPicker } from '../../library/picker/ContentPicker'
import { prefetchPicker } from '../../library/picker/pickerQueries'
import { useLibraryDeps } from '../../library/LibraryDeps'
import { useCrmHostOptional } from '../../crm/CrmHostContext'
import { Spinner, BusyLabel } from '../../components/Spinner'

/** Toolbar panels — vault + device upload, then OnlyFans' poll/price tools. */
export type Panel = 'vault' | 'upload' | 'poll' | 'quiz' | 'expiry' | 'schedule' | 'target' | 'price' | 'tag' | 'labels'

const TOOLS: ReadonlyArray<readonly [Panel, string]> = [
  ['vault', 'folder'],
  ['upload', 'upload'],
  ['poll', 'list'],
  ['quiz', 'help-circle'],
  ['expiry', 'clock'],
  ['schedule', 'calendar'],
  ['target', 'target'],
  ['price', 'dollar-sign'],
  ['tag', 'user-plus'],
]

export const REFERER = `${OF}/posts/create`

/** The toolbar of the inline "Compose new post…" box; each tool opens the composer on its panel. */
export function ToolRow({
  onTool,
  active = {},
  disabled = {},
  accountId,
}: {
  onTool: (p: Panel) => void
  active?: Partial<Record<Panel, boolean>>
  disabled?: Partial<Record<Panel, boolean>>
  /** Resting on "Add media from vault" warms the content picker for this account. */
  accountId?: string
}) {
  const { t } = useTranslation('ofHome')
  const deps = useLibraryDeps()
  const warm = (p: Panel) => p === 'vault' && accountId && prefetchPicker(deps, accountId)
  return (
    <div className="ofh-tools" role="toolbar" aria-label={t('composer.tools')}>
      {TOOLS.map(([p, icon]) => (
        <button
          key={p}
          type="button"
          className={`ofh-tool ${active[p] ? 'is-on' : ''}`}
          aria-label={t(`tool.${p}`)}
          title={t(`tool.${p}`)}
          aria-pressed={active[p] ?? false}
          disabled={disabled[p]}
          onClick={() => onTool(p)}
          onMouseEnter={() => warm(p)}
          onFocus={() => warm(p)}
        >
          <FeatherIcon name={icon} size={19} />
        </button>
      ))}
    </div>
  )
}

interface Props {
  of: OfApi
  /** Keys the content picker's page cache. */
  accountId: string
  /** The panel the composer opened on (a toolbar icon of the inline box). */
  openOn: Panel | null
  /** When it goes out, preset (Queue's "+": the picked day, or saved for later). */
  when?: When
  /** Vault media already attached (the Vault page's "New post" from a selection). */
  media?: MediaRef[]
  onClose: () => void
  /** Sent; OnlyFans' answer and how it was sent. */
  onPosted: (raw: Record<string, unknown>, when: When) => void
}

/** OnlyFans' /posts/create: "← New post" + Post, poll/quiz block, text, labels, toolbar, red error bar. */
export function Composer({ of, accountId, openOn, when = EMPTY_DRAFT.when, media = EMPTY_DRAFT.media, onClose, onPosted }: Props) {
  const { t, i18n } = useTranslation('ofHome')
  const crm = useCrmHostOptional()
  const platform = crm?.crmAccounts.find((a) => a.account_id === accountId)?.platform ?? ''
  const [draft, setDraft] = useState<Draft>(() => {
    const start = { ...EMPTY_DRAFT, when, media }
    return openOn === 'poll' || openOn === 'quiz' ? withPoll(start, openOn) : start
  })
  const [panel, setPanel] = useState<Panel | null>(openOn === 'poll' || openOn === 'quiz' ? null : openOn)
  const [labels, setLabels] = useState<Label[]>([])
  const [tagged, setTagged] = useState<OfUser[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [discard, setDiscard] = useState(false)
  const [uploadHint, setUploadHint] = useState('')
  const set = (patch: Partial<Draft>) => {
    setError('')
    setDraft((d) => ({ ...d, ...patch }))
  }

  const fileRef = useRef<HTMLInputElement>(null)
  const localUrls = useRef<Set<string>>(new Set())
  const tempId = useRef(-Date.now())
  const inflight = useRef(0)
  const cancelled = useRef(new Set<number>())
  const uploading = hasPendingUpload(draft)

  useEffect(
    () => () => {
      for (const u of localUrls.current) URL.revokeObjectURL(u)
      localUrls.current.clear()
    },
    [],
  )

  const dropLocalUrl = (url: string) => {
    if (!url.startsWith('blob:')) return
    URL.revokeObjectURL(url)
    localUrls.current.delete(url)
  }

  const tool = (p: Panel) => {
    if (p === 'poll' || p === 'quiz') {
      set({ poll: draft.poll?.kind === p ? null : (withPoll(draft, p).poll) })
      return
    }
    if (p === 'upload') {
      if (platform === 'fansly') {
        setError(t('err.uploadOnlyfansOnly'))
        return
      }
      // Always allow picking more files while others are still uploading.
      fileRef.current?.click()
      return
    }
    setPanel(p)
  }

  const uploadOne = async (file: File, ph: MediaRef) => {
    inflight.current += 1
    setUploadHint(t('composer.uploading'))
    try {
      if (cancelled.current.has(ph.id)) return
      const up = await uploadToVault(accountId, '', file, (stage) => {
        if (inflight.current > 0) {
          setUploadHint(stage === 'processing' ? t('composer.processing') : t('composer.uploading'))
        }
      })
      if (cancelled.current.has(ph.id)) {
        dropLocalUrl(ph.thumb || '')
        cancelled.current.delete(ph.id)
        return
      }
      const id = Number(up.id)
      if (!Number.isFinite(id) || id <= 0) {
        dropLocalUrl(ph.thumb || '')
        setDraft((d) => ({ ...d, media: d.media.filter((m) => m.id !== ph.id) }))
        return
      }
      setDraft((d) => ({
        ...d,
        media: d.media.map((m) =>
          m.id === ph.id
            ? {
                ...m,
                id,
                type: up.type === 'video' ? 'video' : ('photo' as const),
                pending: false,
              }
            : m,
        ),
      }))
    } catch (e) {
      if (cancelled.current.has(ph.id)) {
        cancelled.current.delete(ph.id)
        return
      }
      dropLocalUrl(ph.thumb || '')
      setDraft((d) => ({ ...d, media: d.media.filter((m) => m.id !== ph.id) }))
      setError(e instanceof Error && e.message ? e.message : t('error'))
    } finally {
      inflight.current = Math.max(0, inflight.current - 1)
      if (inflight.current === 0) setUploadHint('')
    }
  }

  const onDeviceFiles = (files: FileList | null) => {
    if (!files?.length) return
    if (platform === 'fansly') {
      setError(t('err.uploadOnlyfansOnly'))
      return
    }
    setError('')
    const batch = [...files]
    // Do not read flags set inside setDraft updaters — they may run after this function returns.
    // Reserve slots with a local counter; the functional update still guards races / Strict Mode.
    const room = Math.max(0, MEDIA_MAX - draft.media.length)
    if (room <= 0) {
      setError(t('err.mediaMax', { max: MEDIA_MAX, extra: batch.length }))
      if (fileRef.current) fileRef.current.value = ''
      return
    }
    if (batch.length > room) setError(t('err.mediaMax', { max: MEDIA_MAX, extra: batch.length - room }))
    const take = batch.slice(0, room)
    for (let i = 0; i < take.length; i++) {
      const file = take[i]!
      const local = URL.createObjectURL(file)
      localUrls.current.add(local)
      const ph: MediaRef = {
        id: tempId.current--,
        type: file.type.startsWith('video/') ? 'video' : 'photo',
        thumb: local,
        full: local,
        createdAt: '',
        duration: 0,
        inCategories: [],
        addableTo: [],
        preview: false,
        pending: true,
      }
      setDraft((d) => {
        if (d.media.some((m) => m.id === ph.id)) return d
        if (d.media.length >= MEDIA_MAX) {
          cancelled.current.add(ph.id)
          dropLocalUrl(local)
          return d
        }
        const preview = d.price > 0 && d.media.length === 0
        return { ...d, media: [...d.media, { ...ph, preview }] }
      })
      void uploadOne(file, ph)
    }
    if (fileRef.current) fileRef.current.value = ''
  }

  const removeMedia = (id: number) => {
    setDraft((d) => {
      const gone = d.media.find((m) => m.id === id)
      if (gone?.pending) cancelled.current.add(id)
      if (gone?.thumb) dropLocalUrl(gone.thumb)
      return { ...d, media: d.media.filter((m) => m.id !== id) }
    })
  }

  const togglePreview = (id: number) => {
    if (draft.price <= 0) return
    set({ media: draft.media.map((m) => (m.id === id ? { ...m, preview: !m.preview } : m)) })
  }

  const submit = async () => {
    const err = draftError(draft)
    if (err) {
      setError(t(`err.${err}`, { min: err === 'targetMin' ? TARGET_MIN : PRICE_MIN, max: MEDIA_MAX, extra: draft.media.length - MEDIA_MAX }))
      return
    }
    setBusy(true)
    setError('')
    try {
      const raw = await of.send<Record<string, unknown>>('POST', 'posts', REFERER, buildPostBody(draft))
      onPosted(raw ?? {}, draft.when)
    } catch (e) {
      // OnlyFans' own words ("Daily limit exceeded. Please try again later.").
      setError(e instanceof Error && e.message ? e.message : t('error'))
    } finally {
      setBusy(false)
    }
  }

  const back = () => (hasContent(draft) ? setDiscard(true) : onClose())
  const fmtWhen = (at: Date) => at.toLocaleString(i18n.language, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  const sendLabel = draft.when.kind === 'schedule' ? t('composer.schedule') : draft.when.kind === 'later' ? t('composer.saveLater') : t('composer.post')

  return (
    <section className="ofh-composer" aria-label={t('composer.title')}>
      <header className="ofh-head">
        <div className="lib-anchor lib-anchor--start">
          <button type="button" className="lib-icon-btn" aria-label={t('composer.back')} onClick={back}>
            <FeatherIcon name="arrow-left" size={20} />
          </button>
          {discard && (
            <ConfirmPopover
              title={t('composer.discardTitle')}
              body={t('composer.discardBody')}
              confirmLabel={t('composer.discard')}
              onConfirm={onClose}
              onCancel={() => setDiscard(false)}
            />
          )}
        </div>
        <h1 className="ofh-title">{t('composer.title')}</h1>
        <span className="lib-spacer" />
        <button
          type="button"
          className="lib-btn lib-btn--primary ofh-post-btn"
          disabled={busy || uploading || !hasContent(draft)}
          onClick={() => void submit()}
        >
          <BusyLabel busy={busy} label={t('composer.sending')}>{sendLabel}</BusyLabel>
        </button>
      </header>

      {draft.poll && <PollBlock poll={draft.poll} onChange={(poll) => set({ poll })} onRemove={() => set({ poll: null })} />}
      {draft.target !== null && (
        <FundBlock
          target={draft.target}
          tips={draft.tipPresets}
          onOpen={() => setPanel('target')}
          onTips={(tipPresets) => set({ tipPresets })}
          onClear={() => set({ target: null, tipPresets: [] })}
        />
      )}

      <textarea
        className="ofh-editor"
        aria-label={t('composer.text')}
        placeholder={t('composer.placeholder')}
        value={draft.text}
        rows={4}
        autoFocus={!openOn}
        onChange={(e) => set({ text: e.target.value })}
      />

      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        multiple
        hidden
        onChange={(e) => void onDeviceFiles(e.target.files)}
      />

      {draft.price > 0 && draft.media.length > 0 && <p className="ofh-paid-hint">{t('composer.paidHint')}</p>}
      {draft.media.length > 0 && (
        <div className="ofh-attached" aria-label={t('composer.media')}>
          {draft.media.map((m) => (
            <Attached
              key={m.id}
              item={m}
              paid={draft.price > 0}
              onTogglePreview={() => togglePreview(m.id)}
              onRemove={() => removeMedia(m.id)}
            />
          ))}
        </div>
      )}
      {uploadHint && <p className="ofh-uploading">{uploadHint === t('composer.uploading') ? <Spinner size={14} label={uploadHint} /> : uploadHint}</p>}

      <button type="button" className="ofh-labels-btn" onClick={() => setPanel('labels')}>
        <FeatherIcon name="tag" size={15} />
        {labels.length ? labels.map((l) => l.name).join(' · ') : t('labels.add')}
      </button>

      <div className="ofh-settings">
        {draft.price > 0 && (
          <Setting
            icon="dollar-sign"
            text={t('price.chip', { price: draft.price.toFixed(2) })}
            onOpen={() => setPanel('price')}
            onClear={() => set({ price: 0, media: draft.media.map((m) => ({ ...m, preview: false })) })}
          />
        )}
        {draft.expireDays !== null && <Setting icon="clock" text={t('expiry.daysN', { count: draft.expireDays })} onOpen={() => setPanel('expiry')} onClear={() => set({ expireDays: null })} />}
        {draft.when.kind === 'schedule' && <Setting icon="calendar" text={fmtWhen(draft.when.at)} onOpen={() => setPanel('schedule')} onClear={() => set({ when: { kind: 'now' } })} />}
        {draft.when.kind === 'later' && <Setting icon="calendar" text={t('schedule.laterChip')} onOpen={() => setPanel('schedule')} onClear={() => set({ when: { kind: 'now' } })} />}
        {tagged.length > 0 && (
          <Setting icon="user-plus" text={tagged.map((u) => `@${u.username}`).join(', ')} onOpen={() => setPanel('tag')} onClear={() => (setTagged([]), set({ tagged: [] }))} />
        )}
      </div>

      <ToolRow
        onTool={tool}
        accountId={accountId}
        active={{
          vault: draft.media.length > 0,
          poll: draft.poll?.kind === 'poll',
          quiz: draft.poll?.kind === 'quiz',
          expiry: draft.expireDays !== null,
          schedule: draft.when.kind !== 'now',
          target: draft.target !== null,
          price: draft.price > 0,
          tag: tagged.length > 0,
        }}
        disabled={{
          poll: draft.poll?.kind === 'quiz',
          quiz: draft.poll?.kind === 'poll',
          price: draft.target !== null,
          target: draft.price > 0,
        }}
      />
      {error && (
        <p className="ofh-error" role="alert">
          {error}
        </p>
      )}

      {panel === 'vault' && (
        <ContentPicker accountId={accountId} initial={draft.media} max={MEDIA_MAX} onDone={(media: VaultItem[]) => (set({ media }), setPanel(null))} onClose={() => setPanel(null)} />
      )}
      {panel === 'labels' && (
        <LabelsPicker
          of={of}
          chosen={labels}
          onChange={(next) => (setLabels(next), set({ labelIds: next.map((l) => l.id) }))}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === 'tag' && (
        <TagPicker of={of} chosen={tagged} onChange={(next) => (setTagged(next), set({ tagged: next.map((u) => u.id) }))} onClose={() => setPanel(null)} />
      )}
      {panel === 'price' && (
        <AmountDialog
          kind="price"
          value={draft.price || null}
          onSave={(v) => {
            const price = v ?? 0
            setDraft((d) => {
              let media = d.media
              if (price > 0 && !media.some((m) => m.preview)) {
                const i = media.findIndex((m) => !m.pending)
                if (i >= 0) media = media.map((m, j) => (j === i ? { ...m, preview: true } : m))
              }
              if (price <= 0) media = media.map((m) => ({ ...m, preview: false }))
              return { ...d, price, media, ...(price > 0 ? { target: null, tipPresets: [] } : {}) }
            })
            setPanel(null)
          }}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === 'target' && (
        <AmountDialog
          kind="target"
          value={draft.target}
          onSave={(v) => {
            if (v === null) set({ target: null, tipPresets: [] })
            else {
              set({
                target: v,
                price: 0,
                media: draft.media.map((m) => ({ ...m, preview: false })),
                tipPresets: draft.tipPresets.length ? draft.tipPresets : [TIP_PRESET_DEFAULT],
              })
            }
            setPanel(null)
          }}
          onClose={() => setPanel(null)}
        />
      )}
      {panel === 'expiry' && <ExpiryDialog value={draft.expireDays} onSave={(d) => (set({ expireDays: d }), setPanel(null))} onClose={() => setPanel(null)} />}
      {panel === 'schedule' && (
        <ScheduleDialog
          value={draft.when.kind === 'schedule' ? draft.when.at : null}
          onSchedule={(at) => (set({ when: { kind: 'schedule', at } }), setPanel(null))}
          onLater={() => (set({ when: { kind: 'later' } }), setPanel(null))}
          onClose={() => setPanel(null)}
        />
      )}
    </section>
  )
}

function withPoll(d: Draft, kind: 'poll' | 'quiz'): Draft {
  const options = d.poll?.options ?? ['', '']
  return { ...d, poll: { kind, options, correct: null, days: d.poll?.days ?? POLL_DAYS } }
}

/** OnlyFans' fundraising strip: target chip + up to 4 preferred tip amounts. */
function FundBlock({
  target,
  tips,
  onOpen,
  onTips,
  onClear,
}: {
  target: number
  tips: number[]
  onOpen: () => void
  onTips: (tips: number[]) => void
  onClear: () => void
}) {
  const { t } = useTranslation('ofHome')
  const slots = tips.length ? tips : [TIP_PRESET_DEFAULT]
  const setSlot = (i: number, raw: string) => {
    const next = [...slots]
    const n = raw.trim() === '' ? 0 : Number(raw.replace(',', '.'))
    next[i] = Number.isFinite(n) ? Math.round(n * 100) / 100 : 0
    onTips(next)
  }
  const removeSlot = (i: number) => {
    if (slots.length <= 1) {
      onTips([0])
      return
    }
    onTips(slots.filter((_, j) => j !== i))
  }
  return (
    <div className="ofh-fund" aria-label={t('target.title')}>
      <div className="ofh-fund-head">
        <button type="button" className="ofh-fund-target" onClick={onOpen}>
          <FeatherIcon name="target" size={15} />
          {t('target.chip', { amount: target.toFixed(2) })}
        </button>
        <button type="button" className="ofh-fund-x" aria-label={t('composer.clear', { what: t('target.title') })} onClick={onClear}>
          <FeatherIcon name="x" size={14} />
        </button>
      </div>
      <p className="ofh-fund-label">{t('target.tips')}</p>
      <div className="ofh-fund-tips">
        {slots.map((n, i) => (
          <label key={i} className="ofh-fund-tip">
            <span className="ofh-amount-cur">$</span>
            <input
              className="ofp-input"
              inputMode="decimal"
              aria-label={t('target.tipN', { n: i + 1 })}
              placeholder={t('target.tipPlaceholder')}
              value={n > 0 ? String(n) : ''}
              onChange={(e) => setSlot(i, e.target.value.replace(/[^\d.,]/g, ''))}
            />
            <button type="button" className="ofh-fund-tip-x" aria-label={t('target.removeTip', { n: i + 1 })} onClick={() => removeSlot(i)}>
              <FeatherIcon name="x" size={12} />
            </button>
          </label>
        ))}
      </div>
      {slots.length < TIP_PRESETS_MAX && (
        <button type="button" className="ofh-fund-add" onClick={() => onTips([...slots, 0])}>
          <FeatherIcon name="plus" size={14} />
          {t('target.addTip', { left: TIP_PRESETS_MAX - slots.length })}
        </button>
      )}
      <p className="ofh-fund-note">{t('target.tipsNote', { max: TIP_PRESETS_MAX })}</p>
    </div>
  )
}

function PollBlock({ poll, onChange, onRemove }: { poll: NonNullable<Draft['poll']>; onChange: (p: NonNullable<Draft['poll']>) => void; onRemove: () => void }) {
  const { t } = useTranslation('ofHome')
  const setOption = (i: number, v: string) => onChange({ ...poll, options: poll.options.map((o, j) => (j === i ? v : o)) })
  const removeOption = (i: number) =>
    onChange({
      ...poll,
      options: poll.options.length > 2 ? poll.options.filter((_, j) => j !== i) : poll.options.map((o, j) => (j === i ? '' : o)),
      correct: poll.correct === i ? null : poll.correct !== null && poll.correct > i ? poll.correct - 1 : poll.correct,
    })
  return (
    <div className="ofh-poll" role="group" aria-label={t(`tool.${poll.kind}`)}>
      <div className="ofh-poll-head">
        <FeatherIcon name={poll.kind === 'quiz' ? 'help-circle' : 'list'} size={16} />
        <span>{t(`poll.${poll.kind}`)}</span>
        <span className="lib-spacer" />
        <span className="ofh-poll-days">{t('expiry.daysN', { count: poll.days })}</span>
        <button type="button" className="lib-icon-btn ofh-poll-x" aria-label={t('poll.remove')} onClick={onRemove}>
          <FeatherIcon name="x-circle" size={18} />
        </button>
      </div>
      {poll.options.map((o, i) => (
        <div key={i} className="ofh-poll-row">
          {poll.kind === 'quiz' && (
            <input
              type="radio"
              name="ofh-quiz-correct"
              className="ofh-poll-correct"
              aria-label={t('poll.correct', { n: i + 1 })}
              checked={poll.correct === i}
              disabled={!o.trim()}
              onChange={() => onChange({ ...poll, correct: i })}
            />
          )}
          <input
            className="ofp-input"
            aria-label={t('poll.option', { n: i + 1 })}
            placeholder={i === 0 ? t('poll.first') : i === 1 ? t('poll.second') : t('poll.another')}
            value={o}
            maxLength={100}
            onChange={(e) => setOption(i, e.target.value)}
          />
          <button type="button" className="lib-icon-btn" aria-label={t('poll.removeOption', { n: i + 1 })} onClick={() => removeOption(i)}>
            <FeatherIcon name="x" size={15} />
          </button>
        </div>
      ))}
      {poll.options.length < 10 && (
        <button type="button" className="ofp-link-btn ofh-poll-add" onClick={() => onChange({ ...poll, options: [...poll.options, ''] })}>
          + {t('poll.addOption')}
        </button>
      )}
    </div>
  )
}

function Attached({
  item,
  paid,
  onTogglePreview,
  onRemove,
}: {
  item: MediaRef
  paid: boolean
  onTogglePreview: () => void
  onRemove: () => void
}) {
  const { t } = useTranslation('ofHome')
  const thumb = item.thumb ?? ''
  const local = thumb.startsWith('blob:') || thumb.startsWith('data:')
  const { ref, src } = useMediaSrc(local ? '' : thumb)
  const show = local ? thumb : src
  return (
    <span
      ref={ref as React.RefObject<HTMLSpanElement>}
      className={`ofh-att ${paid ? (item.preview ? 'is-preview' : 'is-locked') : ''} ${item.pending ? 'is-pending' : ''}`}
    >
      {show ? (
        item.type === 'video' ? (
          <video src={show} muted playsInline preload="metadata" />
        ) : (
          <img src={show} alt="" />
        )
      ) : (
        <FeatherIcon name={item.type === 'video' ? 'play' : 'image'} size={18} />
      )}
      {item.type === 'video' && (
        <span className="ofh-att-play" aria-hidden="true">
          <FeatherIcon name="play" size={14} />
        </span>
      )}
      {item.pending && <span className="ofh-att-pending"><Spinner size={16} label={t('composer.uploadingShort')} /></span>}
      {paid && !item.pending && (
        <button
          type="button"
          className="ofh-att-access"
          title={item.preview ? t('composer.makeLocked') : t('composer.makePreview')}
          aria-label={item.preview ? t('composer.makeLocked') : t('composer.makePreview')}
          onClick={onTogglePreview}
        >
          <FeatherIcon name={item.preview ? 'eye' : 'lock'} size={11} />
          {item.preview ? t('composer.previewShort') : t('composer.lockedShort')}
        </button>
      )}
      <button type="button" className="ofh-att-x" aria-label={t('composer.removeMedia')} onClick={onRemove}>
        <FeatherIcon name="x" size={12} />
      </button>
    </span>
  )
}

function Setting({ icon, text, onOpen, onClear }: { icon: string; text: string; onOpen: () => void; onClear: () => void }) {
  const { t } = useTranslation('ofHome')
  return (
    <span className="ofh-setting">
      <button type="button" className="ofh-setting-open" onClick={onOpen}>
        <FeatherIcon name={icon} size={13} />
        {text}
      </button>
      <button type="button" className="ofh-setting-x" aria-label={t('composer.clear', { what: text })} onClick={onClear}>
        <FeatherIcon name="x" size={12} />
      </button>
    </span>
  )
}
